// ===============================================================
// ✅ MyProfilePage.js — LIVORA Profile (premium dashboard UI)
// Firebase: single users/{uid} onSnapshot (unchanged semantics)
// Avatar resolve pipeline unchanged
// ===============================================================

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  PiPencilSimpleBold,
  PiCrownSimpleBold,
  PiChatCircleDotsBold,
  PiSparkleBold,
  PiGearSixBold,
  PiUsersThreeBold,
  PiSignOutBold,
  PiHeadsetBold,
  PiXBold,
  PiMedalBold,
  PiRocketLaunchBold,
  PiBellBold,
  PiHeartBold,
  PiWalletBold,
  PiInfoBold,
  PiShieldBold,
  PiCopyBold,
  PiSealCheckBold,
  PiGiftBold,
  PiPhoneBold,
  PiClockBold,
  PiGlobeBold,
  PiCarSimpleBold,
} from "react-icons/pi";
import { doc, onSnapshot, collection, query, where, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./MyProfilePage.css";
import { db } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import { MALE_LEVEL_THRESHOLDS, getMaleLevelName } from "../config/maleLevels";
import BottomNav from "./BottomNav";
import { logoutNow } from "../utils/logoutNow";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import { resolveProfilePhotoUrl } from "../utils/profilePhotoResolver";
import { formatNumberEn } from "../utils/digits";
import goldCoin from "../assets/gold.png";
import tokenImg from "../assets/token.png";
import { CountryFlagGlyph } from "../utils/countryFlags";
import { ENABLE_REAL_MONEY_PAYOUTS } from "../config/featureFlags";
import { ENABLE_COIN_PURCHASES } from "../config/featureFlags";
import { calculateAgeFromBirthDateString } from "../utils/localizedDate";

// Same supported types and unread rule as NotificationsPage.js - keep in
// sync with that file's ALLOWED_TYPES / `item.read !== true` check.
const NOTIFICATION_ALLOWED_TYPES = [
  "follow",
  "visit",
  "gift",
  "withdraw",
  "support",
  "match",
];

function formatNumber(value) {
  return formatNumberEn(Number(value || 0));
}

function formatCompactStat(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  if (n < 1000) return formatNumber(n);
  const k = n / 1000;
  const s = k >= 10 ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, "");
  return `${s}k`;
}

function formatUsdPreviewFromTokens(tokens) {
  const safeTokens = Number(tokens || 0);
  const usd = safeTokens / 100;
  return usd.toFixed(2);
}

function getFemaleUnifiedBalance(data) {
  const tokens = Number(data?.earnings?.availableTokens || 0);
  const usdPreview = formatUsdPreviewFromTokens(tokens);

  return {
    tokens,
    usdPreview,
  };
}

/** Display ID from known fields or compact uid fallback (no backend writes). */
function buildDisplayId(data, uid) {
  const fromData =
    data?.numericId ??
    data?.userNumber ??
    data?.publicUserId ??
    data?.profileId;
  if (fromData != null && String(fromData).trim() !== "") {
    return String(fromData).trim();
  }
  const code = String(data?.inviteCode || "").trim();
  if (code) return code;
  const raw = String(uid || "").replace(/[^0-9a-f]/gi, "");
  const digits = raw.match(/\d/g);
  if (digits && digits.length >= 6) {
    return digits.slice(-7).join("");
  }
  return (uid || "").slice(-8).toUpperCase() || "—";
}

function normalizeFemaleLevels(rules) {
  if (!Array.isArray(rules?.levels) || !rules.levels.length) {
    return [
      { level: 0, requiredTokens: 0, requiredDays: 0 },
      { level: 1, requiredTokens: 100, requiredDays: 0 },
      { level: 2, requiredTokens: 1500, requiredDays: 5 },
      { level: 3, requiredTokens: 4000, requiredDays: 5 },
      { level: 4, requiredTokens: 10000, requiredDays: 5 },
      { level: 5, requiredTokens: 25000, requiredDays: 5 },
      { level: 6, requiredTokens: 70000, requiredDays: 6 },
      { level: 7, requiredTokens: 150000, requiredDays: 6 },
      { level: 8, requiredTokens: 300000, requiredDays: 7 },
      { level: 9, requiredTokens: 550000, requiredDays: 7 },
      { level: 10, requiredTokens: 1000000, requiredDays: 7 },
    ];
  }

  return [...rules.levels]
    .map((item) => ({
      level: Number(item?.level ?? 0),
      requiredTokens: Number(item?.requiredTokens ?? 0),
      requiredDays: Number(item?.requiredDays ?? 0),
    }))
    .sort((a, b) => a.level - b.level);
}

function buildFemaleProgressData(profile, rules) {
  const levels = normalizeFemaleLevels(rules);

  const currentLevel = Number(profile?.femaleLevel ?? 0);
  const cycleTokens = Number(profile?.leveling?.tokens ?? 0);
  const activeDays = Number(profile?.leveling?.activeDays ?? 0);

  const maxLevel = levels.length ? levels[levels.length - 1].level : 0;

  const currentRule =
    levels.find((item) => item.level === currentLevel) || levels[0];

  const nextRule =
    levels.find((item) => item.level === currentLevel + 1) || currentRule;

  const isMaxLevel = currentLevel >= maxLevel;

  const targetTokens = isMaxLevel
    ? Number(currentRule?.requiredTokens ?? 0)
    : Number(nextRule?.requiredTokens ?? 0);

  const requiredDays =
    currentLevel <= 0 || isMaxLevel
      ? 0
      : Number(nextRule?.requiredDays ?? 0);

  const daysLeft =
    requiredDays > 0 ? Math.max(0, requiredDays - activeDays) : 0;

  const tokenProgressPercent =
    targetTokens > 0
      ? Math.max(0, Math.min(100, (cycleTokens / targetTokens) * 100))
      : 0;

  const isReadyByTokens = isMaxLevel ? false : cycleTokens >= targetTokens;
  const isReadyByDays = requiredDays <= 0 ? true : activeDays >= requiredDays;

  return {
    maxLevel,
    currentLevel,
    nextLevel: isMaxLevel ? currentLevel : currentLevel + 1,
    cycleTokens,
    targetTokens,
    activeDays,
    requiredDays,
    daysLeft,
    tokenProgressPercent,
    remainingTokens: Math.max(0, targetTokens - cycleTokens),
    isReady: !isMaxLevel && isReadyByTokens && isReadyByDays,
    isMaxLevel,
    nextRule,
  };
}

export default function MyProfilePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isReady } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [profileFetchKey, setProfileFetchKey] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [birth, setBirth] = useState("");
  const [countryName, setCountryName] = useState("");
  const [flagEmoji, setFlagEmoji] = useState("");
  const [countryKey, setCountryKey] = useState("");
  const [displayCountry, setDisplayCountry] = useState("");
  const [photoRaw, setPhotoRaw] = useState("");
  const [photoURL, setPhotoURL] = useState(() => defaultAvatarByGender(""));

  const [balance, setBalance] = useState(0);
  const [femaleBalance, setFemaleBalance] = useState({
    tokens: 0,
    usdPreview: "0.00",
  });

  const [inviteCount, setInviteCount] = useState(0);
  const [level, setLevel] = useState(0);
  const [premium, setPremium] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const [likesCount, setLikesCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);
  const [friendsCount, setFriendsCount] = useState(0);
  const [verificationApproved, setVerificationApproved] = useState(false);
  const [onlineFlag, setOnlineFlag] = useState(false);
  const [profileDisplayId, setProfileDisplayId] = useState("");
  const [copyHint, setCopyHint] = useState("");

  const [statsTotalCalls, setStatsTotalCalls] = useState(0);
  const [statsTotalCallMinutes, setStatsTotalCallMinutes] = useState(0);
  const [statsTotalCallSeconds, setStatsTotalCallSeconds] = useState(0);
  const [visitedCountriesMetric, setVisitedCountriesMetric] = useState(0);

  const [profileLeveling, setProfileLeveling] = useState({
    tokens: 0,
    activeDays: 0,
  });
  const [malePoints, setMalePoints] = useState(0);
  const [femaleLevelField, setFemaleLevelField] = useState(0);

  const isFemale = (gender || "").toLowerCase() === "female";
  const age = calculateAgeFromBirthDateString(birth);

  useEffect(() => {
    if (!isReady) return;

    if (!user) {
      navigate("/");
      return;
    }

    const userRef = doc(db, "users", user.uid);

    setLoadError(false);

    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setLoadError(false);
          setLoading(false);
          return;
        }

        const data = snap.data() || {};
        const safeGender = String(data.gender || "").toLowerCase();

        setName(data.name || "");
        setGender(data.gender || "");
        setBirth(data.birthDate || data.birth || "");
        setCountryName(
          String(data.countryName || data.country || "").trim()
        );
        setFlagEmoji(String(data.flagEmoji || "").trim());
        setCountryKey((data.countryKey || "").toString().trim().toUpperCase());
        setDisplayCountry(
          (data.displayCountry || "").toString().trim().toUpperCase()
        );
        setInviteCount(Number(data.inviteCount || 0));
        setPhotoRaw(data.profileImage || data.photoURL || "");
        setPremium(data.premium || null);

        setLevel(
          safeGender === "male"
            ? Number(data.maleLevel ?? 0)
            : Number(data.level ?? data.femaleLevel ?? 0)
        );

        setFemaleLevelField(Number(data.femaleLevel ?? data.level ?? 0));

        if (safeGender === "female") {
          const b = getFemaleUnifiedBalance(data);
          setFemaleBalance(b);
          setBalance(b.tokens);
        } else {
          setBalance(
            Number(data.coinsBalance ?? data.coins ?? data.tokens ?? 0)
          );
        }

        setLikesCount(
          Number(
            data.likesCount ??
              data.likes ??
              data.heartsCount ??
              data.heartCount ??
              0
          )
        );
        const st = data.stats || {};

        setFollowersCount(
          Number(
            st.followersCount ??
              data.followersCount ??
              data.followerCount ??
              data.followers ??
              0
          )
        );

        setFriendsCount(Number(st.friendsCount ?? 0));

        const vRaw = String(data.verificationStatus || "").trim().toLowerCase();
        setVerificationApproved(vRaw === "approved");

        setOnlineFlag(Boolean(data.online || data.isOnline));

        setProfileDisplayId(buildDisplayId(data, user.uid));

        setStatsTotalCalls(Number(st.totalCalls ?? data.totalCalls ?? 0));

        // Prefer server aggregate seconds. Fallbacks keep backward compatibility.
        const totalCallSeconds = Number(st.totalCallSeconds ?? 0);
        const legacyMinutes = Number(
          st.totalCallMinutes ??
            st.callMinutes ??
            data.totalCallMinutes ??
            data.totalMinutes ??
            data.callMinutes ??
            0
        );
        const derivedSeconds = totalCallSeconds > 0 ? totalCallSeconds : legacyMinutes * 60;
        setStatsTotalCallMinutes(legacyMinutes);
        setStatsTotalCallSeconds(derivedSeconds);

        const visitedCountField = Number(
          st.visitedCountriesCount ??
            data.visitedCountriesCount ??
            data.visitedCountryCount ??
            0
        );
        const visitedLen = Array.isArray(data.visitedCountries)
          ? data.visitedCountries.length
          : 0;
        setVisitedCountriesMetric(
          visitedCountField > 0 ? visitedCountField : visitedLen
        );

        const lv = data.leveling || {};
        setProfileLeveling({
          tokens: Number(lv.tokens ?? 0),
          activeDays: Number(lv.activeDays ?? 0),
        });

        setMalePoints(Number(data.malePoints ?? data.maleTotalRecharge ?? 0));

        if (!data.gender) {
          navigate("/select-gender");
          return;
        }

        setLoadError(false);
        setLoading(false);
      },
      (err) => {
        console.error("❌ MyProfile snapshot error:", err);
        setLoadError(true);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isReady, user, navigate, profileFetchKey]);

  // ===============================================================
  // 🔔 Profile bell unread dot - read-only, scoped to the signed-in
  // user, same supported types and unread rule as NotificationsPage.js
  // (item.read !== true, so a missing `read` field counts as unread).
  // Never writes/marks anything as read from here. A query error fails
  // safe (count resets to 0, dot stays hidden) rather than showing a
  // false unread dot.
  // ===============================================================
  useEffect(() => {
    if (!isReady || !user) {
      setUnreadNotificationCount(0);
      return;
    }

    const q = query(
      collection(db, "notifications"),
      where("uid", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const unreadCount = snap.docs.reduce((count, d) => {
          const item = d.data();
          const type = String(item?.type || "").trim();
          if (!NOTIFICATION_ALLOWED_TYPES.includes(type)) return count;
          return item?.read !== true ? count + 1 : count;
        }, 0);
        setUnreadNotificationCount(unreadCount);
      },
      (err) => {
        console.error("❌ MyProfile notifications snapshot error:", err);
        setUnreadNotificationCount(0);
      }
    );

    return () => unsub();
  }, [isReady, user]);

  const handleRetryProfile = () => {
    setLoadError(false);
    setLoading(true);
    setProfileFetchKey((key) => key + 1);
  };

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const url = await resolveProfilePhotoUrl(photoRaw || "", gender);
      if (alive) setPhotoURL(url);
    };

    run();

    return () => {
      alive = false;
    };
  }, [photoRaw, gender]);

  useEffect(() => {
    if (!copyHint) return;
    const id = setTimeout(() => setCopyHint(""), 1600);
    return () => clearTimeout(id);
  }, [copyHint]);

  const premiumActive = !!premium?.active;

  const displayedCountryCode =
    /^[A-Z]{2}$/.test(displayCountry)
      ? displayCountry
      : /^[A-Z]{2}$/.test(countryKey)
      ? countryKey
      : "";

  const displayedCountryText = useMemo(() => {
    if (displayedCountryCode) {
      return t(`countries.${displayedCountryCode.toLowerCase()}`, {
        defaultValue: displayedCountryCode,
      });
    }
    return countryName || t("country_unknown", { defaultValue: "—" });
  }, [displayedCountryCode, countryName, t]);

  const walletNavigate = () =>
    isFemale ? navigate("/female-earnings") : navigate("/buy-coins");

  const femaleProgress = useMemo(() => {
    if (!isFemale) return null;
    return buildFemaleProgressData(
      {
        femaleLevel: femaleLevelField || level,
        leveling: profileLeveling,
        earnings: { availableTokens: femaleBalance.tokens },
      },
      null
    );
  }, [isFemale, femaleLevelField, level, profileLeveling, femaleBalance.tokens]);

  const maleProgress = useMemo(() => {
    if (isFemale) return null;
    const pts = malePoints;
    const curLevel = level;
    const curRow =
      MALE_LEVEL_THRESHOLDS.find((x) => x.level === curLevel) ||
      MALE_LEVEL_THRESHOLDS[0];
    const nextRow = MALE_LEVEL_THRESHOLDS.find(
      (x) => x.level === curLevel + 1
    );
    const prevReq = Number(curRow?.required ?? 0);
    const nextReq = nextRow ? Number(nextRow.required) : prevReq;
    const span = Math.max(1, nextReq - prevReq);
    const progressPts = Math.max(0, pts - prevReq);
    const pct = nextRow
      ? Math.min(100, Math.max(0, (progressPts / span) * 100))
      : 100;
    return {
      current: pts,
      target: nextRow ? nextReq : prevReq,
      pct,
      nextLabel: nextRow
        ? getMaleLevelName(nextRow.level)
        : getMaleLevelName(curLevel),
      isMax: !nextRow,
    };
  }, [isFemale, level, malePoints, i18n.language]);

  const levelJourneyTitle = isFemale
    ? t("female_level.overview.title", { defaultValue: "Female level" })
    : t("call_level.title", { defaultValue: "Call level" });

  const callHoursDisplay = useMemo(() => {
    const s = Number(statsTotalCallSeconds || 0);
    const h = s / 3600;
    if (!Number.isFinite(h) || h <= 0) {
      return `0${t("myProfile.hoursSuffix", { defaultValue: "h" })}`;
    }
    const rounded = h >= 10 ? Math.round(h) : Math.round(h * 10) / 10;
    return `${rounded}${t("myProfile.hoursSuffix", { defaultValue: "h" })}`;
  }, [statsTotalCallSeconds, t]);

  const handleCopyId = async () => {
    const text = profileDisplayId || "";
    if (!text || text === "—") return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyHint(
        t("myProfile.copySuccess", { defaultValue: "Copied" })
      );
    } catch {
      setCopyHint(
        t("myProfile.copyFailed", { defaultValue: "Could not copy" })
      );
    }
  };

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      await logoutNow();
      setShowLogoutConfirm(false);
      navigate("/");
    } catch (err) {
      console.error("❌ Logout error:", err);
      setLogoutLoading(false);
      setShowLogoutConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="myprofile-loading">
        <div className="loading-spinner"></div>
        <p>
          {t("loading_profile", {
            defaultValue: "Loading your profile...",
          })}
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="myprofile-page myprofile-dash">
        <div className="myprofile-loading">
          <p>
            {t("myProfile.load_error_title", {
              defaultValue: "Couldn't load your profile",
            })}
          </p>
          <p>
            {t("myProfile.load_error_subtitle", {
              defaultValue: "Check your connection and try again.",
            })}
          </p>
          <button
            type="button"
            className="myprofile-earnings-cta"
            onClick={handleRetryProfile}
          >
            {t("myProfile.retry", { defaultValue: "Try again" })}
          </button>
        </div>
        <BottomNav active="profile" />
      </div>
    );
  }

  const rankLabel = `${t("rank_short", { defaultValue: "R" })}${level}`;

  const balanceMain = isFemale ? femaleBalance.tokens : balance;
  const coinAlt = t("coins.alt", { defaultValue: "Coins" });

  const femaleXpLine = femaleProgress
    ? t("myProfile.xpLine", {
        defaultValue: "{{current}} / {{target}} XP",
        current: formatNumber(femaleProgress.cycleTokens),
        target: formatNumber(femaleProgress.targetTokens),
      })
    : "";

  const maleXpLine = maleProgress
    ? t("myProfile.xpLine", {
        defaultValue: "{{current}} / {{target}} XP",
        current: formatNumber(maleProgress.current),
        target: formatNumber(maleProgress.target),
      })
    : "";

  const progressPercent = isFemale
    ? femaleProgress?.tokenProgressPercent ?? 0
    : maleProgress?.pct ?? 0;

  const nextLevelLabel = isFemale
    ? t("female_level.overview.levelLabel", {
        defaultValue: "Lv{{level}}",
        level: femaleProgress?.nextLevel ?? level,
      })
    : maleProgress?.nextLabel || getMaleLevelName(level);

  const rewardsPath = isFemale
    ? "/female-journey-rewards"
    : "/my-rank-rewards";

  return (
    <div className="myprofile-page myprofile-dash">
      <div className="myprofile-dash-bg" aria-hidden />
      <div className="myprofile-dash-glow myprofile-dash-glow-1" aria-hidden />
      <div className="myprofile-dash-glow myprofile-dash-glow-2" aria-hidden />

      <header className="myprofile-topbar">
        <div className="myprofile-brand" aria-label="Livora">
          Livora
        </div>
        <div className="myprofile-top-actions">
          <button
            type="button"
            className="myprofile-icon-btn"
            onClick={() => navigate("/edit-profile")}
            aria-label={t("edit_profile", { defaultValue: "Edit profile" })}
          >
            <PiPencilSimpleBold />
          </button>
          <button
            type="button"
            className="myprofile-icon-btn"
            onClick={() => navigate("/notifications")}
            aria-label={
              unreadNotificationCount > 0
                ? `${t("myProfile.notificationsAria", {
                    defaultValue: "Notifications",
                  })} — ${t("notifications.unread_count", {
                    defaultValue: "{{count}} unread",
                    count: formatNumberEn(unreadNotificationCount),
                  })}`
                : t("myProfile.notificationsAria", {
                    defaultValue: "Notifications",
                  })
            }
          >
            <PiBellBold />
            {unreadNotificationCount > 0 && (
              <span
                className="myprofile-icon-dot"
                aria-hidden
                data-testid="profile-notification-unread-dot"
              />
            )}
          </button>
        </div>
      </header>

      <section className="myprofile-profile-shell">
        <div className="myprofile-avatar-overlap">
          <div className="myprofile-avatar-ring">
            <div className="myprofile-avatar">
              <img
                src={photoURL || defaultAvatarByGender(gender)}
                alt={name || t("profile.image_alt", { defaultValue: "profile" })}
                onError={(e) => {
                  e.target.src = defaultAvatarByGender(gender);
                }}
              />
            </div>
            <div className="myprofile-rank-badge" aria-label={rankLabel}>
              <PiCrownSimpleBold
                className="myprofile-rank-badge-crown"
                aria-hidden
              />
              <span className="myprofile-rank-badge-text">{rankLabel}</span>
            </div>
          </div>
        </div>

        <div className="myprofile-card myprofile-profile-card">
          <button
            type="button"
            className="myprofile-profile-shortcut-abs"
            onClick={() => navigate("/edit-profile")}
          >
            <span className="myprofile-mini-shortcut-icon">
              <PiSparkleBold />
            </span>
            <span className="myprofile-mini-shortcut-label">
              {t("myProfile.updateProfile", {
                defaultValue: "Update profile",
              })}
            </span>
          </button>

          <div className="myprofile-profile-stat-abs">
            <div className="myprofile-stat-pill">
              <PiHeartBold className="myprofile-stat-pill-i" aria-hidden />
              <span className="myprofile-stat-pill-main">
                {formatCompactStat(followersCount)}
              </span>
              <span className="myprofile-stat-pill-sub">
                {t("profile.heroStats.followersShort", {
                  defaultValue: "Followers",
                })}
              </span>
            </div>
            <div className="myprofile-stat-pill">
              <PiUsersThreeBold className="myprofile-stat-pill-i" aria-hidden />
              <span className="myprofile-stat-pill-main">
                {formatCompactStat(friendsCount)}
              </span>
              <span className="myprofile-stat-pill-sub">
                {t("profile.heroStats.friendsShort", {
                  defaultValue: "Friends",
                })}
              </span>
            </div>
          </div>

          <div className="myprofile-profile-body">
            <div className="myprofile-name-row">
              <span className="myprofile-display-name">
                {name || t("user", { defaultValue: "User" })}
              </span>
              {verificationApproved ? (
                <PiSealCheckBold
                  className="myprofile-verified"
                  aria-label={t("myProfile.verifiedAria", {
                    defaultValue: "Verified",
                  })}
                />
              ) : null}
            </div>

            <button
              type="button"
              className="myprofile-id-row"
              onClick={handleCopyId}
            >
              <span>
                {t("myProfile.idLabel", { defaultValue: "ID:" })}{" "}
                <strong>{profileDisplayId}</strong>
              </span>
              <PiCopyBold className="myprofile-copy-ico" aria-hidden />
            </button>
            {copyHint ? (
              <div className="myprofile-copy-hint" role="status">
                {copyHint}
              </div>
            ) : null}

            {age ? (
              <div className="myprofile-age-pill">
                {/* Explicit Western-digit formatting, not implicit
                    Number-to-string - Arabic UI must never show
                    Arabic-Indic digits for this value. */}
                {t("age_years", { defaultValue: "{{age}} years", age: formatNumberEn(age) })}
              </div>
            ) : null}

            <div className="myprofile-pill-row">
              <span className="myprofile-soft-pill myprofile-soft-pill-country">
                <span className="myprofile-soft-pill-flag" aria-hidden>
                  {displayedCountryCode ? (
                    <CountryFlagGlyph
                      countryCode={displayedCountryCode}
                      imgClassName="country-flag-glyph__img"
                    />
                  ) : flagEmoji ? (
                    flagEmoji
                  ) : (
                    <CountryFlagGlyph />
                  )}
                </span>
                {displayedCountryText}
              </span>

              <span
                className={`myprofile-soft-pill myprofile-soft-pill-premium ${
                  premiumActive ? "is-on" : ""
                }`}
              >
                <PiCrownSimpleBold className="myprofile-soft-pill-ic" aria-hidden />
                {premiumActive
                  ? t("myProfile.premiumActive", {
                      defaultValue: "Livora Premium",
                    })
                  : t("premium.inactive_short", {
                      defaultValue: "Inactive",
                    })}
              </span>

              <span
                className={`myprofile-soft-pill myprofile-soft-pill-online ${
                  onlineFlag ? "is-live" : ""
                }`}
              >
                <span className="myprofile-online-pip" aria-hidden />
                {onlineFlag
                  ? t("presence.online_now", {
                      defaultValue: "Online now",
                    })
                  : t("presence.offline", { defaultValue: "Offline" })}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="myprofile-card myprofile-earnings-card">
        <div className="myprofile-earnings-head">
          <div className="myprofile-earnings-label-wrap">
            <span className="myprofile-earnings-label">
              {isFemale
                ? t("available_earnings", {
                    defaultValue: "My current earnings",
                  })
                : t("my_balance", { defaultValue: "My balance" })}
            </span>
            <span
              className="myprofile-info-ico"
              title={t("myProfile.balanceInfoHint", {
                defaultValue: "Shown from your account balance.",
              })}
              aria-label={t("myProfile.balanceInfoHint", {
                defaultValue: "Shown from your account balance.",
              })}
            >
              <PiInfoBold />
            </span>
          </div>
          <button
            type="button"
            className="myprofile-wallet-mini"
            onClick={walletNavigate}
          >
            <PiWalletBold aria-hidden />
            <span>
              {t("myProfile.myWallet", { defaultValue: "My wallet" })}
            </span>
          </button>
        </div>

        <div className="myprofile-earnings-value-row">
          <span className="myprofile-earnings-num">
            {formatNumber(balanceMain)}
          </span>
          <img
            src={isFemale ? tokenImg : goldCoin}
            alt={coinAlt}
            className="myprofile-earnings-coin"
          />
        </div>

        {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: original "Withdraw
            earnings" label/behavior preserved below, gated so it never
            frames this entry point as cash-out while payouts are
            disabled - still navigates to /female-earnings, which itself
            shows the disabled state and no cash-out UI. */}
        <button
          type="button"
          className="myprofile-earnings-cta"
          onClick={walletNavigate}
        >
          {isFemale
            ? ENABLE_REAL_MONEY_PAYOUTS
              ? t("withdraw_earnings", { defaultValue: "Withdraw earnings" })
              : t("myProfile.viewEarnings", { defaultValue: "View earnings" })
            : ENABLE_COIN_PURCHASES
            ? t("recharge_now", { defaultValue: "Top up now" })
            : t("myProfile.viewBalance", { defaultValue: "View balance" })}
        </button>
      </section>

      <section className="myprofile-shortcuts">
        <div className="myprofile-section-head">
          <span className="myprofile-section-title">
            {t("my_shortcuts", { defaultValue: "My shortcuts" })}
          </span>
          <button
            type="button"
            className="myprofile-text-link"
            onClick={() => navigate("/settings")}
          >
            {t("profile.shortcuts.customizeShort", {
              defaultValue: "Customize",
            })}
          </button>
        </div>

        <div className="myprofile-short-grid myprofile-short-grid-4">
          <button
            type="button"
            className="myprofile-short-tile"
            onClick={() => navigate("/invite")}
          >
            <span className="myprofile-short-ico">
              <PiUsersThreeBold />
            </span>
            <span className="myprofile-short-title">
              {t("profile.shortcuts.inviteShort", {
                defaultValue: "Invite friends",
              })}
            </span>
            <span className="myprofile-short-sub myprofile-short-sub--decorative">
              {t("invite_count_short", {
                defaultValue: "{{count}} invites",
                count: inviteCount,
              })}
            </span>
            <span className="myprofile-short-badge">
              {t("myProfile.inviteBonusPill", { defaultValue: "+50" })}
            </span>
          </button>

          <button
            type="button"
            className="myprofile-short-tile"
            onClick={() => navigate("/my-level")}
          >
            <span className="myprofile-short-ico">
              <PiMedalBold />
            </span>
            <span className="myprofile-short-title">
              {t("profile.shortcuts.rankShort", { defaultValue: "My rank" })}
            </span>
            <span className="myprofile-short-sub myprofile-short-sub--meta">
              {t("profile.shortcuts.levelShort", {
                defaultValue: "Level {{level}}",
                level,
              })}
            </span>
            <span className="myprofile-short-linkish">
              {t("myProfile.viewShort", { defaultValue: "View" })}
            </span>
          </button>

          <button
            type="button"
            className="myprofile-short-tile"
            onClick={() => navigate("/premium")}
          >
            <span className="myprofile-short-ico">
              <PiRocketLaunchBold />
            </span>
            <span className="myprofile-short-title">
              {t("profile.shortcuts.vpnShort", { defaultValue: "VPN" })}
            </span>
            <span className="myprofile-short-sub myprofile-short-sub--decorative">
              {t("profile.shortcuts.locationShort", {
                defaultValue: "Change your location",
              })}
            </span>
            <span className="myprofile-short-linkish">
              {t("myProfile.exploreShort", { defaultValue: "Explore" })}
            </span>
          </button>

          <button
            type="button"
            className="myprofile-short-tile"
            onClick={() =>
              navigate(
                isFemale ? "/female-journey-rewards" : "/my-rank-rewards"
              )
            }
          >
            <span className="myprofile-short-ico">
              <PiCarSimpleBold />
            </span>
            <span className="myprofile-short-title">
              {t("profile.shortcuts.journeyShort", {
                defaultValue: "My Livora journey",
              })}
            </span>
            <span className="myprofile-short-sub myprofile-short-sub--decorative">
              {t("profile.shortcuts.collectShort", {
                defaultValue: "Collect rewards",
              })}
            </span>
            <span className="myprofile-short-linkish">
              {t("myProfile.exploreShort", { defaultValue: "Explore" })}
            </span>
          </button>
        </div>

        <div
          className={`myprofile-short-grid myprofile-short-grid-2 ${
            isFemale ? "cols-3" : "cols-2"
          }`}
        >
          <button
            type="button"
            className="myprofile-short-row2"
            onClick={() => navigate("/settings")}
          >
            <PiGearSixBold aria-hidden />
            <span>
              {t("profile.shortcuts.settingsShort", {
                defaultValue: "Settings",
              })}
            </span>
          </button>
          <button
            type="button"
            className="myprofile-short-row2"
            onClick={() => navigate("/support/history")}
          >
            <PiHeadsetBold aria-hidden />
            <span>
              {t("profile.shortcuts.supportShort", {
                defaultValue: "Support & help",
              })}
            </span>
          </button>
          {isFemale ? (
            <button
              type="button"
              className="myprofile-short-row2"
              onClick={() => navigate("/chat-settings")}
            >
              <PiChatCircleDotsBold aria-hidden />
              <span>
                {t("chat_settings", { defaultValue: "Chat settings" })}
              </span>
            </button>
          ) : null}
        </div>
      </section>

      <section className="myprofile-card myprofile-level-card">
        <div className="myprofile-level-top">
          <div className="myprofile-level-shield" aria-hidden>
            <PiShieldBold />
            <span>{rankLabel}</span>
          </div>
          <div className="myprofile-level-middle">
            <div className="myprofile-level-title">{levelJourneyTitle}</div>
            <div className="myprofile-xp-line">{isFemale ? femaleXpLine : maleXpLine}</div>
            <div className="myprofile-progress-track" aria-hidden>
              <div
                className="myprofile-progress-fill"
                style={{ width: `${Math.min(100, progressPercent)}%` }}
              />
            </div>
            <div className="myprofile-level-next">
              {t("myProfile.nextLevelLine", {
                defaultValue: "Next: {{label}}",
                label: nextLevelLabel,
              })}
            </div>
          </div>
          <button
            type="button"
            className="myprofile-rewards-btn"
            onClick={() => navigate(rewardsPath)}
          >
            <PiGiftBold aria-hidden />
            <span>
              {t("myProfile.levelRewards", {
                defaultValue: "Level rewards",
              })}
            </span>
          </button>
        </div>
      </section>

      <section className="myprofile-card myprofile-activity-card">
        <div className="myprofile-section-head myprofile-activity-head">
          <span className="myprofile-section-title">
            {t("profile.activity.titleShort", {
              defaultValue: "Your activity",
            })}
          </span>
          <span className="myprofile-activity-filter">
            {t("myProfile.last7Days", { defaultValue: "Last 7 days" })}
          </span>
        </div>
        <div className="myprofile-activity-cols">
          <div className="myprofile-activity-item">
            <PiPhoneBold className="myprofile-activity-i" aria-hidden />
            <div className="myprofile-activity-label">
              {t("profile.activity.callsShort", {
                defaultValue: "Calls",
              })}
            </div>
            <div className="myprofile-activity-val">
              {formatNumber(statsTotalCalls)}
            </div>
          </div>
          <div className="myprofile-activity-item">
            <PiClockBold className="myprofile-activity-i" aria-hidden />
            <div className="myprofile-activity-label">
              {t("profile.activity.callHoursShort", {
                defaultValue: "Call hours",
              })}
            </div>
            <div className="myprofile-activity-val">{callHoursDisplay}</div>
          </div>
          <div className="myprofile-activity-item">
            <PiGlobeBold className="myprofile-activity-i" aria-hidden />
            <div className="myprofile-activity-label">
              {t("profile.activity.countriesShort", {
                defaultValue: "Countries used",
              })}
            </div>
            <div className="myprofile-activity-val">
              {formatNumber(visitedCountriesMetric)}
            </div>
          </div>
        </div>
      </section>

      <section className="myprofile-card myprofile-logout-strip-card">
        <button
          type="button"
          className="myprofile-logout-strip"
          onClick={() => setShowLogoutConfirm(true)}
        >
          <span className="myprofile-logout-strip-icon-wrap" aria-hidden>
            <PiSignOutBold className="myprofile-logout-strip-icon" />
          </span>
          <span className="myprofile-logout-strip-copy">
            <span className="myprofile-logout-strip-title">
              {t("logout", { defaultValue: "Log out" })}
            </span>
            <span className="myprofile-logout-strip-sub">
              {t("myProfile.logoutSessionHint", {
                defaultValue: "End the session safely",
              })}
            </span>
          </span>
        </button>
      </section>

      {showLogoutConfirm && (
        <div
          className="dashboard-modal-backdrop"
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            className="dashboard-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="dashboard-modal-close"
              onClick={() => setShowLogoutConfirm(false)}
              aria-label={t("close", { defaultValue: "Close" })}
            >
              <PiXBold />
            </button>

            <div className="dashboard-modal-title">
              {t("logout_confirm_title", {
                defaultValue: "Log out of your account?",
              })}
            </div>

            <div className="dashboard-modal-text">
              {t("logout_confirm_text", {
                defaultValue: "You can sign in again anytime.",
              })}
            </div>

            <div className="dashboard-modal-actions">
              <button
                type="button"
                className="dashboard-modal-btn dashboard-modal-cancel"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>

              <button
                type="button"
                className="dashboard-modal-btn dashboard-modal-danger"
                onClick={handleLogout}
                disabled={logoutLoading}
              >
                {logoutLoading
                  ? t("logging_out", { defaultValue: "Logging out..." })
                  : t("logout", { defaultValue: "Log out" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav active="profile" />
    </div>
  );
}
